/**
 * Platform tags (admin-defined) render filled and cannot be removed by rooms.
 * Room-defined tags render outlined and removable. See CLAUDE.md "Locked product rules".
 *
 * The notch on the trailing edge is direction-owned: --tag-notch is 7px in The Rail, where
 * notched chips are a signature, and 0px in The Ledger, where chips are square. One token,
 * correct in both, no direction awareness in this component.
 */
export function TagPill({
  label,
  kind = 'room',
  onRemove,
}: {
  label: string;
  kind?: 'platform' | 'room';
  onRemove?: () => void;
}) {
  const isPlatform = kind === 'platform';
  const removable = !isPlatform && typeof onRemove === 'function';

  if (isPlatform) {
    return (
      <span
        className="inline-flex items-center gap-1.5 bg-ink-950 py-1 pl-2.5 text-[11px] font-bold uppercase tracking-[0.07em] text-ground"
        style={{
          paddingRight: 'calc(0.625rem + var(--tag-notch))',
          clipPath:
            'polygon(0 0, 100% 0, calc(100% - var(--tag-notch)) 100%, 0 100%)',
        }}
        title="Platform tag — set by the platform admin and not removable here"
      >
        <span aria-label="Platform tag">&#128274;</span>
        {label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 border-[1.5px] border-ink-950 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.07em] text-ink-950">
      {label}
      {removable ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          className="text-ink-800 transition hover:text-bad"
        >
          &times;
        </button>
      ) : null}
    </span>
  );
}
